import type {EventsNetworkSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/EventsNetwork';
import type {PerspectiveCameraObjNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/PerspectiveCamera';
import type {GeoObjNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {RootManagerNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ReturnedNodeDataDict} from '../../ExampleUtils';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import type {MaterialsNetworkSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import type {CopNetworkObjNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/CopNetwork';
import {ColorConversion} from '../../../../../@polygonjs/polygonjs/src/core/Color';
import type {DecomposeSopNode} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/Decompose';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	const scene_root_nodes: ReturnedNodeDataDict = {};
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		const geo1_nodes: ReturnedNodeDataDict = {};
		function create_decompose1(geo1: GeoObjNode) {
			var decompose1 = geo1.createNode('decompose');
			decompose1.setName('decompose1');
			const decompose1_nodes: ReturnedNodeDataDict = {};
			function create_COP(decompose1: DecomposeSopNode) {
				var COP = decompose1.createNode('copNetwork');
				COP.setName('COP');
				const COP_nodes: ReturnedNodeDataDict = {};
				if (COP.childrenController) {
					COP.childrenController.selection.set([]);
				}
				COP.uiData.setPosition(0, 0);
				COP.params.postCreateSpareParams();
				COP.params.runOnSceneLoadHooks();
				return {node: COP, children: COP_nodes};
			}
			function create_MAT(decompose1: DecomposeSopNode) {
				var MAT = decompose1.createNode('materialsNetwork');
				MAT.setName('MAT');
				const MAT_nodes: ReturnedNodeDataDict = {};
				function create_meshStandard__export_prep_mesh_0(MAT: MaterialsNetworkSopNode) {
					var meshStandard__export_prep_mesh_0 = MAT.createNode('meshStandard');
					meshStandard__export_prep_mesh_0.setName('meshStandard__export_prep_mesh_0');
					const meshStandard__export_prep_mesh_0_nodes: ReturnedNodeDataDict = {};
					meshStandard__export_prep_mesh_0.uiData.setPosition(0, 0);
					meshStandard__export_prep_mesh_0.p.color.set([0, 1, 0.3843137254901961]);
					meshStandard__export_prep_mesh_0.p.color.setConversion(ColorConversion.NONE);
					meshStandard__export_prep_mesh_0.p.useVertexColors.set(true);
					meshStandard__export_prep_mesh_0.p.emissive.setConversion(ColorConversion.NONE);
					meshStandard__export_prep_mesh_0.p.useEnvMap.set(true);
					meshStandard__export_prep_mesh_0.p.envMap.set('../../../../COP/envMap');
					meshStandard__export_prep_mesh_0.p.normalScale.set([1, -1]);
					meshStandard__export_prep_mesh_0.p.metalness.set(0.88);
					meshStandard__export_prep_mesh_0.p.roughness.set(0.1);
					meshStandard__export_prep_mesh_0.params.postCreateSpareParams();
					meshStandard__export_prep_mesh_0.params.runOnSceneLoadHooks();
					return {node: meshStandard__export_prep_mesh_0, children: meshStandard__export_prep_mesh_0_nodes};
				}
				function create_meshStandard__export_prep_mesh_0_1(MAT: MaterialsNetworkSopNode) {
					var meshStandard__export_prep_mesh_0_1 = MAT.createNode('meshStandard');
					meshStandard__export_prep_mesh_0_1.setName('meshStandard__export_prep_mesh_0_1');
					const meshStandard__export_prep_mesh_0_1_nodes: ReturnedNodeDataDict = {};
					meshStandard__export_prep_mesh_0_1.uiData.setPosition(200, 0);
					meshStandard__export_prep_mesh_0_1.p.color.set([0, 0, 0]);
					meshStandard__export_prep_mesh_0_1.p.color.setConversion(ColorConversion.NONE);
					meshStandard__export_prep_mesh_0_1.p.useVertexColors.set(true);
					meshStandard__export_prep_mesh_0_1.p.emissive.setConversion(ColorConversion.NONE);
					meshStandard__export_prep_mesh_0_1.p.normalScale.set([1, -1]);
					meshStandard__export_prep_mesh_0_1.p.roughness.set(-0.151999965);
					meshStandard__export_prep_mesh_0_1.params.postCreateSpareParams();
					meshStandard__export_prep_mesh_0_1.params.runOnSceneLoadHooks();
					return {
						node: meshStandard__export_prep_mesh_0_1,
						children: meshStandard__export_prep_mesh_0_1_nodes,
					};
				}
				function create_meshStandard__export_prep_mesh_0_2(MAT: MaterialsNetworkSopNode) {
					var meshStandard__export_prep_mesh_0_2 = MAT.createNode('meshStandard');
					meshStandard__export_prep_mesh_0_2.setName('meshStandard__export_prep_mesh_0_2');
					const meshStandard__export_prep_mesh_0_2_nodes: ReturnedNodeDataDict = {};
					meshStandard__export_prep_mesh_0_2.uiData.setPosition(400, 0);
					meshStandard__export_prep_mesh_0_2.p.color.set([0.588235319, 0.588235319, 0.588235319]);
					meshStandard__export_prep_mesh_0_2.p.color.setConversion(ColorConversion.NONE);
					meshStandard__export_prep_mesh_0_2.p.useVertexColors.set(true);
					meshStandard__export_prep_mesh_0_2.p.emissive.setConversion(ColorConversion.NONE);
					meshStandard__export_prep_mesh_0_2.p.normalScale.set([1, -1]);
					meshStandard__export_prep_mesh_0_2.p.roughness.set(-0.151999965);
					meshStandard__export_prep_mesh_0_2.params.postCreateSpareParams();
					meshStandard__export_prep_mesh_0_2.params.runOnSceneLoadHooks();
					return {
						node: meshStandard__export_prep_mesh_0_2,
						children: meshStandard__export_prep_mesh_0_2_nodes,
					};
				}
				function create_meshStandard__export_prep_mesh_0_3(MAT: MaterialsNetworkSopNode) {
					var meshStandard__export_prep_mesh_0_3 = MAT.createNode('meshStandard');
					meshStandard__export_prep_mesh_0_3.setName('meshStandard__export_prep_mesh_0_3');
					const meshStandard__export_prep_mesh_0_3_nodes: ReturnedNodeDataDict = {};
					meshStandard__export_prep_mesh_0_3.uiData.setPosition(600, 0);
					meshStandard__export_prep_mesh_0_3.p.color.set([0.00999999978, 0.00999999978, 0.00999999978]);
					meshStandard__export_prep_mesh_0_3.p.color.setConversion(ColorConversion.NONE);
					meshStandard__export_prep_mesh_0_3.p.useVertexColors.set(true);
					meshStandard__export_prep_mesh_0_3.p.emissive.setConversion(ColorConversion.NONE);
					meshStandard__export_prep_mesh_0_3.p.normalScale.set([1, -1]);
					meshStandard__export_prep_mesh_0_3.p.roughness.set(-0.151999965);
					meshStandard__export_prep_mesh_0_3.params.postCreateSpareParams();
					meshStandard__export_prep_mesh_0_3.params.runOnSceneLoadHooks();
					return {
						node: meshStandard__export_prep_mesh_0_3,
						children: meshStandard__export_prep_mesh_0_3_nodes,
					};
				}
				function create_meshStandard__export_prep_mesh_0_4(MAT: MaterialsNetworkSopNode) {
					var meshStandard__export_prep_mesh_0_4 = MAT.createNode('meshStandard');
					meshStandard__export_prep_mesh_0_4.setName('meshStandard__export_prep_mesh_0_4');
					const meshStandard__export_prep_mesh_0_4_nodes: ReturnedNodeDataDict = {};
					meshStandard__export_prep_mesh_0_4.uiData.setPosition(800, 0);
					meshStandard__export_prep_mesh_0_4.p.color.set([0.00999999978, 0.00999999978, 0.00999999978]);
					meshStandard__export_prep_mesh_0_4.p.color.setConversion(ColorConversion.NONE);
					meshStandard__export_prep_mesh_0_4.p.useVertexColors.set(true);
					meshStandard__export_prep_mesh_0_4.p.emissive.setConversion(ColorConversion.NONE);
					meshStandard__export_prep_mesh_0_4.p.normalScale.set([1, -1]);
					meshStandard__export_prep_mesh_0_4.p.roughness.set(-0.151999965);
					meshStandard__export_prep_mesh_0_4.params.postCreateSpareParams();
					meshStandard__export_prep_mesh_0_4.params.runOnSceneLoadHooks();
					return {
						node: meshStandard__export_prep_mesh_0_4,
						children: meshStandard__export_prep_mesh_0_4_nodes,
					};
				}
				MAT_nodes['meshStandard__export_prep_mesh_0'] = create_meshStandard__export_prep_mesh_0(MAT);
				MAT_nodes['meshStandard__export_prep_mesh_0_1'] = create_meshStandard__export_prep_mesh_0_1(MAT);
				MAT_nodes['meshStandard__export_prep_mesh_0_2'] = create_meshStandard__export_prep_mesh_0_2(MAT);
				MAT_nodes['meshStandard__export_prep_mesh_0_3'] = create_meshStandard__export_prep_mesh_0_3(MAT);
				MAT_nodes['meshStandard__export_prep_mesh_0_4'] = create_meshStandard__export_prep_mesh_0_4(MAT);
				if (MAT.childrenController) {
					MAT.childrenController.selection.set([]);
				}
				MAT.uiData.setPosition(200, 0);
				MAT.params.postCreateSpareParams();
				MAT.params.runOnSceneLoadHooks();
				return {node: MAT, children: MAT_nodes};
			}
			function create_material__export_prep_mesh_0(decompose1: DecomposeSopNode) {
				var material__export_prep_mesh_0 = decompose1.createNode('material');
				material__export_prep_mesh_0.setName('material__export_prep_mesh_0');
				const material__export_prep_mesh_0_nodes: ReturnedNodeDataDict = {};
				material__export_prep_mesh_0.uiData.setPosition(400, -1000);
				material__export_prep_mesh_0.p.group.set('/export_prep/mesh_0');
				material__export_prep_mesh_0.p.material.set('../MAT/meshStandard__export_prep_mesh_0');
				material__export_prep_mesh_0.params.postCreateSpareParams();
				material__export_prep_mesh_0.params.runOnSceneLoadHooks();
				return {node: material__export_prep_mesh_0, children: material__export_prep_mesh_0_nodes};
			}
			function create_material__export_prep_mesh_0_1(decompose1: DecomposeSopNode) {
				var material__export_prep_mesh_0_1 = decompose1.createNode('material');
				material__export_prep_mesh_0_1.setName('material__export_prep_mesh_0_1');
				const material__export_prep_mesh_0_1_nodes: ReturnedNodeDataDict = {};
				material__export_prep_mesh_0_1.uiData.setPosition(400, -800);
				material__export_prep_mesh_0_1.p.group.set('/export_prep/mesh_0_1');
				material__export_prep_mesh_0_1.p.material.set('../MAT/meshStandard__export_prep_mesh_0_1');
				material__export_prep_mesh_0_1.params.postCreateSpareParams();
				material__export_prep_mesh_0_1.params.runOnSceneLoadHooks();
				return {node: material__export_prep_mesh_0_1, children: material__export_prep_mesh_0_1_nodes};
			}
			function create_material__export_prep_mesh_0_2(decompose1: DecomposeSopNode) {
				var material__export_prep_mesh_0_2 = decompose1.createNode('material');
				material__export_prep_mesh_0_2.setName('material__export_prep_mesh_0_2');
				const material__export_prep_mesh_0_2_nodes: ReturnedNodeDataDict = {};
				material__export_prep_mesh_0_2.uiData.setPosition(400, -600);
				material__export_prep_mesh_0_2.p.group.set('/export_prep/mesh_0_2');
				material__export_prep_mesh_0_2.p.material.set('../MAT/meshStandard__export_prep_mesh_0_2');
				material__export_prep_mesh_0_2.params.postCreateSpareParams();
				material__export_prep_mesh_0_2.params.runOnSceneLoadHooks();
				return {node: material__export_prep_mesh_0_2, children: material__export_prep_mesh_0_2_nodes};
			}
			function create_material__export_prep_mesh_0_3(decompose1: DecomposeSopNode) {
				var material__export_prep_mesh_0_3 = decompose1.createNode('material');
				material__export_prep_mesh_0_3.setName('material__export_prep_mesh_0_3');
				const material__export_prep_mesh_0_3_nodes: ReturnedNodeDataDict = {};
				material__export_prep_mesh_0_3.uiData.setPosition(400, -400);
				material__export_prep_mesh_0_3.p.group.set('/export_prep/mesh_0_3');
				material__export_prep_mesh_0_3.p.material.set('../MAT/meshStandard__export_prep_mesh_0_3');
				material__export_prep_mesh_0_3.params.postCreateSpareParams();
				material__export_prep_mesh_0_3.params.runOnSceneLoadHooks();
				return {node: material__export_prep_mesh_0_3, children: material__export_prep_mesh_0_3_nodes};
			}
			function create_material__export_prep_mesh_0_4(decompose1: DecomposeSopNode) {
				var material__export_prep_mesh_0_4 = decompose1.createNode('material');
				material__export_prep_mesh_0_4.setName('material__export_prep_mesh_0_4');
				const material__export_prep_mesh_0_4_nodes: ReturnedNodeDataDict = {};
				material__export_prep_mesh_0_4.uiData.setPosition(400, -200);
				material__export_prep_mesh_0_4.p.group.set('/export_prep/mesh_0_4');
				material__export_prep_mesh_0_4.p.material.set('../MAT/meshStandard__export_prep_mesh_0_4');
				material__export_prep_mesh_0_4.params.postCreateSpareParams();
				material__export_prep_mesh_0_4.params.runOnSceneLoadHooks();
				return {node: material__export_prep_mesh_0_4, children: material__export_prep_mesh_0_4_nodes};
			}
			function create_subnetInput1(decompose1: DecomposeSopNode) {
				var subnetInput1 = decompose1.createNode('subnetInput');
				subnetInput1.setName('subnetInput1');
				const subnetInput1_nodes: ReturnedNodeDataDict = {};
				subnetInput1.uiData.setPosition(400, -1200);
				subnetInput1.flags.display.set(true);
				subnetInput1.params.postCreateSpareParams();
				subnetInput1.params.runOnSceneLoadHooks();
				return {node: subnetInput1, children: subnetInput1_nodes};
			}
			function create_subnetOutput1(decompose1: DecomposeSopNode) {
				var subnetOutput1 = decompose1.createNode('subnetOutput');
				subnetOutput1.setName('subnetOutput1');
				const subnetOutput1_nodes: ReturnedNodeDataDict = {};
				subnetOutput1.uiData.setPosition(400, 0);
				subnetOutput1.params.postCreateSpareParams();
				subnetOutput1.params.runOnSceneLoadHooks();
				return {node: subnetOutput1, children: subnetOutput1_nodes};
			}
			decompose1_nodes['COP'] = create_COP(decompose1);
			decompose1_nodes['MAT'] = create_MAT(decompose1);
			decompose1_nodes['material__export_prep_mesh_0'] = create_material__export_prep_mesh_0(decompose1);
			decompose1_nodes['material__export_prep_mesh_0_1'] = create_material__export_prep_mesh_0_1(decompose1);
			decompose1_nodes['material__export_prep_mesh_0_2'] = create_material__export_prep_mesh_0_2(decompose1);
			decompose1_nodes['material__export_prep_mesh_0_3'] = create_material__export_prep_mesh_0_3(decompose1);
			decompose1_nodes['material__export_prep_mesh_0_4'] = create_material__export_prep_mesh_0_4(decompose1);
			decompose1_nodes['subnetInput1'] = create_subnetInput1(decompose1);
			decompose1_nodes['subnetOutput1'] = create_subnetOutput1(decompose1);
			decompose1_nodes['material__export_prep_mesh_0'].node.setInput(0, decompose1_nodes['subnetInput1'].node);
			decompose1_nodes['material__export_prep_mesh_0_1'].node.setInput(
				0,
				decompose1_nodes['material__export_prep_mesh_0'].node
			);
			decompose1_nodes['material__export_prep_mesh_0_2'].node.setInput(
				0,
				decompose1_nodes['material__export_prep_mesh_0_1'].node
			);
			decompose1_nodes['material__export_prep_mesh_0_3'].node.setInput(
				0,
				decompose1_nodes['material__export_prep_mesh_0_2'].node
			);
			decompose1_nodes['material__export_prep_mesh_0_4'].node.setInput(
				0,
				decompose1_nodes['material__export_prep_mesh_0_3'].node
			);
			decompose1_nodes['subnetOutput1'].node.setInput(0, decompose1_nodes['material__export_prep_mesh_0_4'].node);
			if (decompose1.childrenController) {
				decompose1.childrenController.selection.set([decompose1_nodes['material__export_prep_mesh_0'].node]);
			}
			decompose1.uiData.setPosition(-450, 50);
			decompose1.uiData
				.setComment(`Clicking on the **decompose** button will create material nodes for each material imported by the file node above.

This will then allow you to edit the materials separately, giving you control on how you want this asset to look into your scene.

This is much more convenient than having to re-export the asset.`);
			decompose1.flags.display.set(true);
			decompose1.params.postCreateSpareParams();
			decompose1.params.runOnSceneLoadHooks();
			return {node: decompose1, children: decompose1_nodes};
		}
		function create_fileGLTF1(geo1: GeoObjNode) {
			var fileGLTF1 = geo1.createNode('fileGLTF');
			fileGLTF1.setName('fileGLTF1');
			const fileGLTF1_nodes: ReturnedNodeDataDict = {};
			fileGLTF1.uiData.setPosition(-450, -100);
			fileGLTF1.p.url.set('https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/models/car.glb');
			fileGLTF1.params.postCreateSpareParams();
			fileGLTF1.params.runOnSceneLoadHooks();
			return {node: fileGLTF1, children: fileGLTF1_nodes};
		}
		geo1_nodes['decompose1'] = create_decompose1(geo1);
		geo1_nodes['fileGLTF1'] = create_fileGLTF1(geo1);
		geo1_nodes['decompose1'].node.setInput(0, geo1_nodes['fileGLTF1'].node);
		if (geo1.childrenController) {
			geo1.childrenController.selection.set([]);
		}
		geo1.uiData.setPosition(-50, -150);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return {node: geo1, children: geo1_nodes};
	}
	function create_grid(parentNode: RootManagerNode) {
		var grid = parentNode.createNode('geo');
		grid.setName('grid');
		const grid_nodes: ReturnedNodeDataDict = {};
		function create_planeHelper1(grid: GeoObjNode) {
			var planeHelper1 = grid.createNode('planeHelper');
			planeHelper1.setName('planeHelper1');
			const planeHelper1_nodes: ReturnedNodeDataDict = {};
			planeHelper1.uiData.setPosition(0, 0);
			planeHelper1.flags.display.set(true);
			planeHelper1.params.postCreateSpareParams();
			planeHelper1.params.runOnSceneLoadHooks();
			return {node: planeHelper1, children: planeHelper1_nodes};
		}
		grid_nodes['planeHelper1'] = create_planeHelper1(grid);
		if (grid.childrenController) {
			grid.childrenController.selection.set([]);
		}
		grid.uiData.setPosition(-50, -250);
		grid.flags.display.set(true);
		grid.params.postCreateSpareParams();
		grid.params.runOnSceneLoadHooks();
		return {node: grid, children: grid_nodes};
	}
	function create_hemisphereLight1(parentNode: RootManagerNode) {
		var hemisphereLight1 = parentNode.createNode('hemisphereLight');
		hemisphereLight1.setName('hemisphereLight1');
		const hemisphereLight1_nodes: ReturnedNodeDataDict = {};
		hemisphereLight1.uiData.setPosition(150, 100);
		hemisphereLight1.flags.display.set(true);
		hemisphereLight1.params.postCreateSpareParams();
		hemisphereLight1.params.runOnSceneLoadHooks();
		return {node: hemisphereLight1, children: hemisphereLight1_nodes};
	}
	function create_perspectiveCamera1(parentNode: RootManagerNode) {
		var perspectiveCamera1 = parentNode.createNode('perspectiveCamera');
		perspectiveCamera1.setName('perspectiveCamera1');
		const perspectiveCamera1_nodes: ReturnedNodeDataDict = {};
		function create_events1(perspectiveCamera1: PerspectiveCameraObjNode) {
			var events1 = perspectiveCamera1.createNode('eventsNetwork');
			events1.setName('events1');
			const events1_nodes: ReturnedNodeDataDict = {};
			function create_cameraOrbitControls1(events1: EventsNetworkSopNode) {
				var cameraOrbitControls1 = events1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				const cameraOrbitControls1_nodes: ReturnedNodeDataDict = {};
				cameraOrbitControls1.uiData.setPosition(150, 50);
				cameraOrbitControls1.p.target.set([-0.049216274401858016, 0.33854019567917704, -0.10690729322940476]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return {node: cameraOrbitControls1, children: cameraOrbitControls1_nodes};
			}
			events1_nodes['cameraOrbitControls1'] = create_cameraOrbitControls1(events1);
			if (events1.childrenController) {
				events1.childrenController.selection.set([]);
			}
			events1.uiData.setPosition(-200, 50);
			events1.params.postCreateSpareParams();
			events1.params.runOnSceneLoadHooks();
			return {node: events1, children: events1_nodes};
		}
		perspectiveCamera1_nodes['events1'] = create_events1(perspectiveCamera1);
		if (perspectiveCamera1.childrenController) {
			perspectiveCamera1.childrenController.selection.set([]);
		}
		perspectiveCamera1.uiData.setPosition(-200, 100);
		perspectiveCamera1.flags.display.set(true);
		perspectiveCamera1.p.t.set([3.0161526633531355, 1.1787657307191874, 2.814738208647524]);
		perspectiveCamera1.p.r.set([-22.723127930556515, 44.66532130223666, 16.403892417511617]);
		perspectiveCamera1.p.controls.set('./events1/cameraOrbitControls1');
		perspectiveCamera1.params.postCreateSpareParams();
		perspectiveCamera1.params.runOnSceneLoadHooks();
		return {node: perspectiveCamera1, children: perspectiveCamera1_nodes};
	}
	function create_COP(parentNode: RootManagerNode) {
		var COP = parentNode.createNode('copNetwork');
		COP.setName('COP');
		const COP_nodes: ReturnedNodeDataDict = {};
		function create_envMap(COP: CopNetworkObjNode) {
			var envMap = COP.createNode('envMap');
			envMap.setName('envMap');
			const envMap_nodes: ReturnedNodeDataDict = {};
			envMap.uiData.setPosition(50, 200);
			envMap.params.postCreateSpareParams();
			envMap.params.runOnSceneLoadHooks();
			return {node: envMap, children: envMap_nodes};
		}
		function create_imageEnv(COP: CopNetworkObjNode) {
			var imageEnv = COP.createNode('imageEXR');
			imageEnv.setName('imageEnv');
			const imageEnv_nodes: ReturnedNodeDataDict = {};
			imageEnv.uiData.setPosition(50, 100);
			imageEnv.p.url.set(
				'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/piz_compressed.exr'
			);
			imageEnv.params.postCreateSpareParams();
			imageEnv.params.runOnSceneLoadHooks();
			return {node: imageEnv, children: imageEnv_nodes};
		}
		function create_imageUv(COP: CopNetworkObjNode) {
			var imageUv = COP.createNode('image');
			imageUv.setName('imageUv');
			const imageUv_nodes: ReturnedNodeDataDict = {};
			imageUv.uiData.setPosition(-100, 100);
			imageUv.p.url.set('https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/uv.jpg');
			imageUv.p.tflipY.set(true);
			imageUv.params.postCreateSpareParams();
			imageUv.params.runOnSceneLoadHooks();
			return {node: imageUv, children: imageUv_nodes};
		}
		COP_nodes['envMap'] = create_envMap(COP);
		COP_nodes['imageEnv'] = create_imageEnv(COP);
		COP_nodes['imageUv'] = create_imageUv(COP);
		COP_nodes['envMap'].node.setInput(0, COP_nodes['imageEnv'].node);
		if (COP.childrenController) {
			COP.childrenController.selection.set([]);
		}
		COP.uiData.setPosition(-200, 200);
		COP.params.postCreateSpareParams();
		COP.params.runOnSceneLoadHooks();
		return {node: COP, children: COP_nodes};
	}
	scene_root_nodes['geo1'] = create_geo1(root);
	scene_root_nodes['grid'] = create_grid(root);
	scene_root_nodes['hemisphereLight1'] = create_hemisphereLight1(root);
	scene_root_nodes['perspectiveCamera1'] = create_perspectiveCamera1(root);
	scene_root_nodes['COP'] = create_COP(root);

	return {
		scene,
		cameraPath: scene_root_nodes['perspectiveCamera1'].node.path(),
		focusedNodes: scene_root_nodes['geo1'].node.children(),
	};
}
// export const sopDecompose_basic = [
// 	new Example({
// 		name: `${ExamplePrefix.BY_NODE}/sop/Decompose/basic`,
// 		createData,
// 		relatedNodes: [DecomposeSopNode],
// 		categories: [ExampleCategory.ASSETS],
// 		label: 'Edit Materials After Import',
// 	}),
// ];
